"use client";

import { useEffect, useRef } from "react";

const HEARTBEAT_INTERVAL_MS = 60_000;

export function useViewTracking(documentId: string | null | undefined) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const controllerRef = useRef<AbortController | null>(null);
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!documentId) return;
    const endpoint = `/api/documents/${documentId}/view-tracking`;

    // The server records a fixed SQL-side 10-second sample for each ping. Do
    // not send a client duration: it would be forgeable and would change the
    // historical sampling contract (60-second ping, 10-second increment).
    const ping = () => {
      if (navigator.sendBeacon?.(endpoint)) return;
      if (inFlightRef.current) return;

      const controller = new AbortController();
      controllerRef.current = controller;
      inFlightRef.current = true;

      fetch(endpoint, {
        method: "POST",
        credentials: "include",
        keepalive: true,
        signal: controller.signal,
      }).catch(() => {
        // Non-fatal -- view tracking is best-effort
      }).finally(() => {
        if (controllerRef.current === controller) {
          controllerRef.current = null;
          inFlightRef.current = false;
        }
      });
    };

    // First ping on mount
    ping();

    intervalRef.current = setInterval(ping, HEARTBEAT_INTERVAL_MS);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      controllerRef.current?.abort();
      controllerRef.current = null;
      inFlightRef.current = false;
    };
  }, [documentId]);
}
